import React, { useState, useCallback, useEffect } from "react";
import styled from "styled-components";
import ReactMarkdown from "react-markdown";
import { adminApi } from "../../utils/axiosConfig";

const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

const defaultFormData = {
  name: "",
  description: "",
  price: "",
  promo: "", 
  type: "femme",
  variantes: [
    {
      couleur: "",
      images: [null], // <-- Au moins un champ image par variante au départ
      stock: [{ taille: "", quantity: 0 }],
    },
  ],
};


// Styled-components
const FormContainer = styled.form`
  max-width: 900px;
  margin: 0 auto;
  padding: 2rem;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
`;

const Title = styled.h2`
  color: #2c3e50;
  text-align: center;
  margin-bottom: 2rem;
  font-size: 1.8rem;
  font-weight: 600;
`;

const SectionTitle = styled.h3`
  color: #2c3e50;
  margin: 1.5rem 0 1rem;
  font-size: 1.3rem;
  font-weight: 500;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: #4a5568;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: #4299e1;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.2);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 1rem;
  min-height: 100px;

  &:focus {
    outline: none;
    border-color: #4299e1;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.2);
  }
`;

const MarkdownPreview = styled.div`
  background-color: #f9fafb;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 1rem;
  margin-top: 0.75rem;
  font-size: 1rem;
  line-height: 1.6;
  color: #2d3748;

  ul {
    padding-left: 1.5rem;
    list-style-type: disc;
  }

  ol {
    padding-left: 1.5rem;
    list-style-type: decimal;
  }

  pre {
    background-color: #edf2f7;
    padding: 0.5rem;
    border-radius: 4px;
    overflow-x: auto;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 1rem;
`;

const VariantCard = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  background: #f8fafc;
`;

const Button = styled.button`
  padding: 0.6rem 1.2rem;
  background-color: ${(props) =>
    props.primary ? "#4299e1" : props.danger ? "#e53e3e" : "#edf2f7"};
  color: ${(props) => (props.primary || props.danger ? "white" : "#2d3748")};
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  margin-right: 0.5rem;
  margin-bottom: 0.5rem;

  &:hover {
    background-color: ${(props) =>
      props.primary ? "#3182ce" : props.danger ? "#c53030" : "#e2e8f0"};
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const SmallButton = styled(Button)`
  padding: 0.3rem 0.6rem;
  font-size: 0.8rem;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #e2e8f0;
  margin: 1.5rem 0;
`;

const StockRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;

  input[type="text"] {
    flex: 2;
  }

  input[type="number"] {
    flex: 1;
    max-width: 100px;
  }
`;

const Message = styled.div`
  padding: 0.75rem 1rem;
  border-radius: 6px;
  margin-top: 1rem;
  background-color: ${(props) => (props.success ? "#ebf8ff" : "#fff5f5")};
  color: ${(props) => (props.success ? "#2b6cb0" : "#c53030")};
  border: 1px solid ${(props) => (props.success ? "#bee3f8" : "#fed7d7")};
`;

const ImageUploadWrapper = styled.div`
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export default function AddProduct() {
  const [formData, setFormData] = useState(defaultFormData);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const handleProductChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleVarianteChange = (index, field, value) => {
    const newVariantes = [...formData.variantes];
    newVariantes[index][field] = value;
    setFormData((prev) => ({ ...prev, variantes: newVariantes }));
  };

  const handleImageFileChange = (vIndex, imgIndex, file) => {
    const newVariantes = [...formData.variantes];
    newVariantes[vIndex].images[imgIndex] = file;
    setFormData((prev) => ({ ...prev, variantes: newVariantes }));
  };

  const addVariante = () => {
    setFormData((prev) => ({
      ...prev,
      variantes: [
        ...prev.variantes,
        { couleur: "", images: [], stock: [{ taille: "", quantity: 0 }] },
      ],
    }));
  };

  const removeVariante = (index) => {
    const newVariantes = formData.variantes.filter((_, i) => i !== index);
    setFormData((prev) => ({
      ...prev,
      variantes:
        newVariantes.length > 0 ? newVariantes : [defaultFormData.variantes[0]],
    }));
  };

  const addImageField = (vIndex) => {
    const newVariantes = [...formData.variantes];
    newVariantes[vIndex].images.push(null);
    setFormData((prev) => ({ ...prev, variantes: newVariantes }));
  };

  const removeImageField = (vIndex, imgIndex) => {
    const newVariantes = [...formData.variantes];
    newVariantes[vIndex].images = newVariantes[vIndex].images.filter(
      (_, i) => i !== imgIndex
    );
    if (newVariantes[vIndex].images.length === 0)
      newVariantes[vIndex].images.push(null);
    setFormData((prev) => ({ ...prev, variantes: newVariantes }));
  };

  const handleStockChange = (vIndex, sIndex, field, value) => {
    const newVariantes = [...formData.variantes];
    newVariantes[vIndex].stock[sIndex][field] =
      field === "quantity" ? Number(value) : value;
    setFormData((prev) => ({ ...prev, variantes: newVariantes }));
  };

  const addStockField = (vIndex) => {
    const newVariantes = [...formData.variantes];
    newVariantes[vIndex].stock.push({ taille: "", quantity: 0 });
    setFormData((prev) => ({ ...prev, variantes: newVariantes }));
  };

  const removeStockField = (vIndex, sIndex) => {
    const newVariantes = [...formData.variantes];
    newVariantes[vIndex].stock = newVariantes[vIndex].stock.filter(
      (_, i) => i !== sIndex
    );
    if (newVariantes[vIndex].stock.length === 0)
      newVariantes[vIndex].stock.push({ taille: "", quantity: 0 });
    setFormData((prev) => ({ ...prev, variantes: newVariantes }));
  };

  const validateForm = () => {
    if (!formData.name.trim() || !formData.price || !formData.type)
      return "Nom, prix et type requis";

    if (
      isNaN(formData.promo) ||
      Number(formData.promo) < 0 ||
      Number(formData.promo) > 100
    )
      return "La promotion doit être un pourcentage entre 0 et 100";

    for (const [vIndex, variante] of formData.variantes.entries()) {
      if (!variante.couleur.trim())
        return `Couleur manquante pour variante ${vIndex + 1}`;
      if (
        !Array.isArray(variante.images) ||
        variante.images.length === 0 ||
        variante.images.some((img) => !img || !(img instanceof File))
      ) {
        return `Chaque image / modèle de la couleur ${
          variante.couleur || vIndex + 1
        } est requise`;
      }

      for (const [sIndex, stock] of variante.stock.entries()) {
        if (!stock.taille.trim())
          return `Taille manquante dans la variante ${vIndex + 1}`;
        if (isNaN(stock.quantity) || stock.quantity < 0)
          return `Quantité invalide dans la variante ${vIndex + 1}, taille ${
            stock.taille
          }`;
      }
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);

    const error = validateForm();
    if (error) {
      setMessage(error);
      return;
    }

    try {
      setLoading(true);
      const form = new FormData();

      const variantesWithFieldnames = formData.variantes.map((v, vIndex) => ({
        couleur: v.couleur.trim(),
        images: v.images.map((_, imgIndex) => `image-${vIndex}-${imgIndex}`),
        stock: v.stock.map((s) => ({
          taille: s.taille.trim(),
          quantity: Number(s.quantity),
        })),
      }));

      formData.variantes.forEach((v, vIndex) => {
        v.images.forEach((file, imgIndex) => {
          if (file) {
            form.append(`image-${vIndex}-${imgIndex}`, file);
          }
        });
      });

      form.append("name", formData.name.trim());
      form.append("description", formData.description.trim());
      form.append("price", formData.price);
      form.append("promo", formData.promo || 0); // 👈 ajouté
      form.append("type", formData.type);
      form.append("variantes", JSON.stringify(variantesWithFieldnames));

      await adminApi.post(`/api/admin/products`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setMessage("Produit ajouté avec succès");
      setFormData(JSON.parse(JSON.stringify(defaultFormData)));
    } catch (err) {
      setMessage(
        err.response?.data?.message || "Erreur lors de l'ajout du produit"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormContainer onSubmit={handleSubmit}>
      <Title>Ajouter un nouveau produit</Title>

      <FormGroup>
        <Label htmlFor="name">Nom du produit</Label>
        <Input
          id="name"
          name="name"
          value={formData.name}
          onChange={handleProductChange}
          placeholder="Ex: Votre Babouche"
          required
        />
      </FormGroup>

      <FormGroup>
        <Label htmlFor="description">Description</Label>
        <TextArea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleProductChange}
          placeholder={`Ex: Bonjour,\nVoici les détails:\n- Taille 38\n- Fait main\n- Livraison offerte`}
        />
      </FormGroup>

      {formData.description.trim() && (
        <div style={{ marginTop: "1rem" }}>
          <Label>Aperçu formaté :</Label>
          <MarkdownPreview>
            <ReactMarkdown>{formData.description}</ReactMarkdown>
          </MarkdownPreview>
        </div>
      )}

      <FormGroup>
        <Label htmlFor="price">Prix (MAD)</Label>
        <Input
          type="number"
          id="price"
          name="price"
          value={formData.price}
          onChange={handleProductChange}
          step="0.01"
          placeholder="Ex: 249.99"
          required
        />
      </FormGroup>

      <FormGroup>
        <Label htmlFor="promo">Promotion (%)</Label>
        <Input
          type="number"
          id="promo"
          name="promo"
          value={formData.promo}
          onChange={handleProductChange}
          placeholder="Ex: 20"
          min="0"
          max="100"
        />
        {formData.promo &&
          formData.price &&
          !isNaN(formData.promo) &&
          !isNaN(formData.price) && (
            <p
              style={{
                color: "#2b6cb0",
                fontWeight: "500",
                marginTop: "0.5rem",
              }}
            >
              Prix après réduction :{" "}
              {(formData.price * (1 - formData.promo / 100)).toFixed(2)} MAD
            </p>
          )}
      </FormGroup>

      <FormGroup>
        <Label htmlFor="type">Catégorie</Label>
        <Select
          id="type"
          name="type"
          value={formData.type}
          onChange={handleProductChange}
          required
        >
          <option value="femme">Femme</option>
          <option value="homme">Homme</option>
          <option value="enfant">Enfant</option>
          <option value="collections speciales">Collections Spéciales</option>
        </Select>
      </FormGroup>

      <Divider />
      <SectionTitle>Variantes de produit</SectionTitle>

      {formData.variantes.map((v, vIndex) => (
        <VariantCard key={vIndex}>
          <FormGroup>
            <Label>Couleur</Label>
            <Input
              type="text"
              value={v.couleur}
              onChange={(e) =>
                handleVarianteChange(vIndex, "couleur", e.target.value)
              }
              placeholder="Ex: Rouge, Bleu marine..."
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>Images / Modèles 3D</Label>
            {v.images.map((img, imgIndex) => (
              <ImageUploadWrapper key={imgIndex}>
                <Input
                  type="file"
                  accept="image/*,.obj,.gltf,.glb"
                  onChange={(e) =>
                    handleImageFileChange(vIndex, imgIndex, e.target.files[0])
                  }
                />
                {v.images.length > 1 && (
                  <SmallButton
                    type="button"
                    danger
                    onClick={() => removeImageField(vIndex, imgIndex)}
                  >
                    Supprimer
                  </SmallButton>
                )}
              </ImageUploadWrapper>
            ))}
            <Button type="button" onClick={() => addImageField(vIndex)}>
              + Ajouter une image/modèle
            </Button>
          </FormGroup>

          <FormGroup>
            <Label>Gestion des stocks</Label>
            {v.stock.map((s, sIndex) => (
              <StockRow key={sIndex}>
                <Input
                  type="text"
                  placeholder="Taille (Ex: 38,39,40..)"
                  value={s.taille}
                  onChange={(e) =>
                    handleStockChange(vIndex, sIndex, "taille", e.target.value)
                  }
                  required
                />
                <Input
                  type="number"
                  placeholder="Quantité"
                  value={s.quantity}
                  onChange={(e) =>
                    handleStockChange(
                      vIndex,
                      sIndex,
                      "quantity",
                      e.target.value
                    )
                  }
                  min="0"
                  required
                />
                {v.stock.length > 1 && (
                  <SmallButton
                    type="button"
                    danger
                    onClick={() => removeStockField(vIndex, sIndex)}
                  >
                    Supprimer
                  </SmallButton>
                )}
              </StockRow>
            ))}
            <Button type="button" onClick={() => addStockField(vIndex)}>
              + Ajouter une taille
            </Button>
          </FormGroup>

          {formData.variantes.length > 1 && (
            <Button
              type="button"
              danger
              onClick={() => removeVariante(vIndex)}
              style={{ marginTop: "1rem" }}
            >
              Supprimer cette variante
            </Button>
          )}
        </VariantCard>
      ))}

      <Button
        type="button"
        onClick={addVariante}
        style={{ marginBottom: "1.5rem" }}
      >
        + Ajouter une variante de couleur
      </Button>

      <div style={{ textAlign: "center" }}>
        <Button
          type="submit"
          primary
          disabled={loading}
          style={{ padding: "0.75rem 2rem", fontSize: "1rem" }}
        >
          {loading ? "Enregistrement en cours..." : "Enregistrer le produit"}
        </Button>
      </div>

      {message && (
        <Message success={message.includes("succès")}>{message}</Message>
      )}
    </FormContainer>
  );
}
